import { VdFeatureManager } from '@vend/vend.js'
import angular, { IPromise } from 'angular'
import each from 'lodash/each'
import find from 'lodash/find'
import filter from 'lodash/filter'
import sortBy from 'lodash/sortBy'
import includes from 'lodash/includes'
import moment from 'moment-timezone'
import uuid from '@vend/vend-uuid'
import { divide } from 'vend-number'
import {
  ApiOutlet,
  ApiReceiptSettings,
  ApiRegister,
  ApiUser,
} from '@vend/types'

import { trackError } from '@vend/utilities'
import type { VdCurrencySettings } from '../vend-ui/types'
import { cleanExtend } from '../factories/helpers/cleanExtend'
import { $q } from '../ng-compat/imports'
import { SyncData } from '../factories/database/entity-store'
import { outletsStore } from '../factories/database/outlets-store'
import { registersStore } from '../factories/database/registers-store'
import { UsersStore } from '../factories/database/users-store'
import { RegistersResource } from '../components/register/registers-resource/registers-resource.service'
import { EventEmitter } from '../util/event-emitter'
import { ReceiptSettingsStore } from '../factories/database/receipt-settings-store'
import { FEATURE_XMEN2565_RECEIPT_WEB_PRESENCE } from '../constants/wr-features'
import {
  filterEligibleCrossLocationFulfilmentTargetOutlets,
  isSourceOutletCrossLocationFulfilmentEnabled,
  isTargetOutletCrossLocationFulfilmentEligible,
} from '../util/cross-location-fulfilment'
import {
  preCacheQrForItems,
  WebPresenceItem,
} from '../services/web-presence-service'
import { PosStateManager } from './pos-state-manager'

const CASH_DISCOUNTING = 'cash_discounting_rate'

const TRAINING_MODE_INVOICE_NUMBER = 99999

interface ActiveOutletChangeEvent {
  activeOutlet: ApiOutlet
}

interface ActiveRegisterChangeEvent {
  activeRegister: ApiRegister
}

interface RegisterEvent {
  register: ApiRegister
}

interface CloseRegisterReminderSeenEvent {
  registerId: string
}

type Events = {
  activeOutletChange: [ActiveOutletChangeEvent]
  activeRegisterChange: [ActiveRegisterChangeEvent]
  activeRegisterClose: [RegisterEvent]
  activeRegisterOpen: [RegisterEvent]
  registerIdChange: [RegisterEvent]
  closeRegisterReminderSeen: [CloseRegisterReminderSeenEvent]
}

/**
 * Factory for managing registers.
 *
 * @class RegisterManager
 * @constructor
 * @extends EventEmitter
 */
export class RegisterManager extends EventEmitter<Events> {
  private _activeOutlet: $TSFixMe
  private _activeRegister: $TSFixMe
  private _activeRegisterReceiptLogo: $TSFixMe
  private _activeRegisterReceiptSettings: $TSFixMe
  private _emulateRegisterOpen: $TSFixMe
  private _allUsersList: ApiUser[]
  private _waitForReceiptStoresToRefresh: $TSFixMe
  private _whenInitialised: $TSFixMe

  constructor(
    private posStateManager: PosStateManager,
    private receiptSettingsStore: ReceiptSettingsStore,
    private registersResource: RegistersResource,
    private wrDebugTools: $TSFixMe,
    private vdCurrencySettings: VdCurrencySettings,
    private vdFeatureManager: VdFeatureManager,
    private usersStore: UsersStore
  ) {
    'ngInject'
    super()
    this.wrDebugTools.register('registerManager')
    this._activeOutlet = null
    this._activeRegister = null
    this._allUsersList = []
    this._activeRegisterReceiptSettings = {}
    this._activeRegisterReceiptLogo = null
    // Used to emulate the register being open when in training mode
    this._emulateRegisterOpen = false
    outletsStore.on('sync', this._onOutletsSync, this)
    registersStore.on('sync', this._onRegistersSync, this)
    this.usersStore.on('sync', this._onUsersSync, this)
    this.receiptSettingsStore.on('sync', this._onReceiptSettingsSync, this)
    this.on('activeRegisterChange', this._onActiveRegisterChange, this)
    this.posStateManager.on(
      'trainingModeEnabledChange',
      this._onTrainingModeEnabledChange,
      this
    )
  }

  // ============================== Public API ============================== //
  /**
   * Initialises the register manager, retrieving the active register and outlet.
   *
   * @method init
   *
   * @return {Promise} A promise resolved with the register manager instance once initialisation is complete
   */
  public init(): IPromise<this> {
    if (!this._whenInitialised) {
      this._whenInitialised = this.posStateManager
        .init()
        .then(() => {
          const registerId = this.posStateManager.getRegisterId()
          this._emulateRegisterOpen = this.posStateManager.getTrainingModeEnabled()
          const activeOutletId = this.posStateManager.getOutletId()!
          if (registerId) {
            return $q.all({
              register: registersStore.get(registerId),
              outlet: outletsStore.get(activeOutletId),
              allOutlets: outletsStore.all(),
              users: this._getAllUsersByActiveOutlet(activeOutletId),
            })
          }
        })
        .then(data => {
          if (data && data.register && data.outlet) {
            this._setActiveRegister(data.register)
            this._setActiveOutlet(data.outlet)
            this._setAllUsers(data.users)
          }
          return this
        })
        .catch(error => {
          trackError(
            '[RegisterManager] Failed to initialise register manager',
            { data: { error } }
          )

          return $q.reject(error)
        })
    }
    return this._whenInitialised
  }

  /**
   * Get all the retailer's registers and outlets for the current user. The result is formatted as an array of
   * outlet register info containing an `outlet` property with the outlet info and a `registers` property with the
   * array of registers for that outlet.
   *
   * @method getAllRegistersByOutlet
   *
   * @param {Object} options
   *
   *        @param {Array} [options.restrictedOutlets]
   *               Restrict results to only these provided outlet ids.
   *
   * @return {Promise} A promise resolved with an array of the retailer's registers and outlets
   */
  public getAllRegistersByOutlet(
    options: {
      restrictedOutlets?: string[]
    } = {}
  ): IPromise<unknown> {
    const { restrictedOutlets = [] } = options
    return registersStore.all().then(registers => {
      const registersByOutletId: Record<
        string,
        { outlet?: ApiOutlet; registers: ApiRegister[] }
      > = {}
      const outletsPromises: $TSFixMe[] = []
      each(registers, register => {
        const outletId = register.outlet_id
        if (
          !restrictedOutlets.length ||
          includes(restrictedOutlets, outletId)
        ) {
          let outletRegisterInfo = registersByOutletId[outletId]
          if (!outletRegisterInfo) {
            outletRegisterInfo = {
              registers: [register],
            }
            registersByOutletId[outletId] = outletRegisterInfo
            outletsPromises.push(
              outletsStore.get(outletId).then(outlet => {
                outletRegisterInfo.outlet = outlet
                return outletRegisterInfo
              })
            )
          } else {
            outletRegisterInfo.registers.push(register)
          }
        }
      })
      return $q
        .all(outletsPromises)
        .then(registersByOutlet =>
          this._sortRegistersByOutlet(registersByOutlet)
        )
    })
  }

  /**
   * Set the given register as the active register. Saves the register ID and associated outlet ID to the POS
   * state manager.
   *
   * @method setActiveRegister
   *
   * @param register {Register}
   *        The register to set as the active register
   *
   * @param outlet {Outlet}
   *        The outlet the register belongs to
   */
  public setActiveRegister(register: ApiRegister, outlet: ApiOutlet): void {
    if (outlet.id !== register.outlet_id) {
      trackError('Selected register does not belong to selected outlet', {
        data: { register, outlet },
      })
    }
    this._setActiveRegister(register)
    this._setActiveOutlet(outlet)
    this._getAllUsersByActiveOutlet(outlet.id).then((users: ApiUser[]) =>
      this._setAllUsers(users)
    )
    this.posStateManager.setRegisterId(register.id)
    this.posStateManager.setOutletId(outlet.id)
    this.setLastUsedReceiptNumber(register.invoice_sequence)
  }

  /**
   * Get the current active register.
   *
   * @method getActiveRegister
   *
   * @return {Register} The current active register
   */
  public getActiveRegister(): ApiRegister {
    return this._activeRegister
  }

  /**
   * Get a register from the register store by Id
   *
   * @method getRegisterById
   *
   * @param  {String} registerId
   *
   * @return {Register}
   */
  public getRegisterById(
    registerId: string
  ): IPromise<ApiRegister | undefined> {
    return registersStore.get(registerId)
  }

  /**
   * Get a outlet from the outlet store by Id
   *
   * @method getOutletById
   *
   * @param  {String} outletId
   *
   * @return {Outlet}
   */
  public getOutletById(outletId: string): IPromise<ApiOutlet | undefined> {
    return outletsStore.get(outletId)
  }

  /**
   * Get the outlet the current active register belongs to.
   *
   * @method getActiveOutlet
   *
   * @return {Outlet} The current active outlet
   */
  public getActiveOutlet(): ApiOutlet {
    return this._activeOutlet
  }

  public isSourceOutletCLFEnabled(): boolean {
    return isSourceOutletCrossLocationFulfilmentEnabled(this._activeOutlet)
  }

  public isTargetOutletCLFEligible(outlet: ApiOutlet): boolean {
    return isTargetOutletCrossLocationFulfilmentEligible(outlet)
  }

  public getEligibleCLFTargetOutlets(outlets: ApiOutlet[]): ApiOutlet[] {
    return filterEligibleCrossLocationFulfilmentTargetOutlets(outlets)
  }

  /**
   * Get list of all users of current outlet (includes deleted and inactive users).
   *
   * @method getAllUsers
   *
   * @return {ApiUser[]} All users in current active outlet
   */
  public getAllUsers(): ApiUser[] {
    return this._allUsersList
  }

  /**
   * Returns a user by id
   *
   * @param id
   *
   * @returns {ApiUser}
   */
  public getUserById(id: string): ApiUser | null {
    const user = this._allUsersList.filter(user => user.id === id)
    return user.length === 1 ? user[0] : null
  }

  /**
   * Determine if the active register is open.
   *
   * @method isActiveRegisterOpen
   *
   * @return {Boolean} True if there is an open, active register, false otherwise
   */
  public isActiveRegisterOpen(): boolean {
    return (
      this._emulateRegisterOpen ||
      Boolean(this._activeRegister && this._activeRegister.is_open)
    )
  }

  /**
   * Whether to display prices inclusive of tax or not - determined from a setting on the current active
   * outlet.
   *
   * @method isTaxInclusive
   *
   * @return {Boolean} True if prices should be displayed inclusive of tax, false otherwise
   */
  public isTaxInclusive(): boolean {
    return Boolean(
      this._activeOutlet && this._activeOutlet.display_prices === 'inclusive'
    )
  }

  public isLowInventoryWarningEnabled(): boolean {
    if (!this._activeOutlet || !this._activeOutlet.attributes) {
      return false
    }
    const lowInventoryWarning = find(this._activeOutlet.attributes, {
      key: 'low_stock_warning',
    })
    return lowInventoryWarning && lowInventoryWarning.value === '1'
  }

  /**
   * Whether to display the attach customer tooltip on the sale summary screen - determined
   * from a setting on the current active outlet.
   *
   * @method isAddCustomerEnabled
   *
   * @return {Boolean} True if the attach customer tooltip should be displayed, false otherwise
   */
  public isAddCustomerEnabled(): boolean {
    if (!this._activeOutlet || !this._activeOutlet.attributes) {
      return false
    }
    const addCustomerPrompt = find(this._activeOutlet.attributes, {
      key: 'add_customer_prompt',
    })
    return !addCustomerPrompt || addCustomerPrompt.value === '1'
  }

  /**
   * Wait for a register to have been selected.
   *
   * @method waitForSelectedRegister
   *
   * @return {Promise} A promise resolved once a register has been selected
   */
  public waitForSelectedRegister(): IPromise<unknown> {
    return $q(resolve => {
      if (this._activeRegister) {
        resolve(this._activeRegister)
      } else {
        this.once('activeRegisterChange', (event: $TSFixMe) => {
          resolve(event.activeRegister)
        })
      }
    })
  }

  /**
   * Wait for the active register to be set and opened.
   *
   * @method waitForOpenRegister
   *
   * @return {Promise<Register>} A promise resolved with the active, open register
   */
  public waitForOpenRegister(): IPromise<unknown> {
    const registerDeferred = $q.defer()
    function resolveOnRegisterOpen(event: $TSFixMe) {
      registerDeferred.resolve(event.register)
    }
    if (this.isActiveRegisterOpen()) {
      registerDeferred.resolve(this._activeRegister)
    } else if (this._activeRegister) {
      this.once('activeRegisterOpen', resolveOnRegisterOpen)
    } else {
      this.posStateManager.once('registerIdChange', event => {
        if (
          !this._activeRegister ||
          this._activeRegister.id !== event.newValue
        ) {
          trackError('POS state register ID does not match selected register', {
            data: {
              activeRegister: this._activeRegister,
              updatedRegisterId: event.newValue,
            },
          })
        }
        if (this.isActiveRegisterOpen()) {
          registerDeferred.resolve(this._activeRegister)
        } else {
          this.once('activeRegisterOpen', resolveOnRegisterOpen)
        }
      })
    }
    return registerDeferred.promise
  }

  /**
   * Whether the retailer has cash discounting enabled and a rate set.
   *
   * @method hasCashDiscounting
   *
   * @return {Boolean} true if cash discounting is enabled
   */
  public hasCashDiscounting(): boolean {
    return Boolean(
      this.vdFeatureManager.get('cash_discounting') &&
        this._getActiveOutletAttribute(CASH_DISCOUNTING)
    )
  }

  /**
   * Get the cash discounting rate as a decimal for the active outlet, if one is set.
   *
   * @return {String|undefined} The cash discounting rate, or undefined if none is set
   */
  public getCashDiscountingRate(): string {
    const cashDiscountingRate = this._getActiveOutletAttribute(CASH_DISCOUNTING)
    return cashDiscountingRate && divide(cashDiscountingRate, 100).toString()
  }

  /**
   * Get the raw cash discounting rate.
   *
   * @return {String|undefined} The raw cash discounting rate, or undefined if none is set
   */
  public getDisplayCashDiscountingRate(): string | undefined {
    return this._getActiveOutletAttribute(CASH_DISCOUNTING)
  }

  /**
   * Get the next available receipt number to assign to a sale including the configured prefix and suffix.
   * Increments the last used receipt number tracked in the POS State manager. If the register is in training
   * mode, then don't increment the invoice number.
   *
   * @method getNextReceiptNumberDetailsAndIncrement
   *
   * @return {ReceiptNumberDetails} Object containing the `prefix`, `suffix` and `number` for the next receipt
   *         number
   */
  public getNextReceiptNumberDetailsAndIncrement(): {
    number: number
    prefix: string
    suffix: string
  } {
    let nextReceiptNumber = this.getLastUsedReceiptNumber()! + 1
    if (this.posStateManager.getTrainingModeEnabled()) {
      nextReceiptNumber = TRAINING_MODE_INVOICE_NUMBER
    } else {
      this.setLastUsedReceiptNumber(nextReceiptNumber)
    }
    return {
      number: nextReceiptNumber,
      prefix: this._activeRegister.invoice_prefix,
      suffix: this._activeRegister.invoice_suffix,
    }
  }

  /**
   * Return the logo image URL for the active register's default receipt template.
   * If the settings change, or if the current active register changes,
   * the same object reference is updated.
   *
   * @return {String}
   */
  public getActiveRegisterReceiptLogo(): string {
    return this._activeRegisterReceiptLogo
  }

  /**
   * Return a reference to the receipt settings of the active register. If the settings change, or if the
   * current active register changes, the same object reference is updated.
   *
   * @return {ReceiptSettings}
   */
  public getActiveRegisterReceiptSettings(): unknown {
    return this._activeRegisterReceiptSettings
  }

  /**
   * Responsible for setting the current quick key button layout to the provided layout.
   *
   * @method setCurrentQuickKeyLayout
   *
   * @param quickKeyLayout {quickKeyLayout}
   *        The quick key button layout which is to be set as the current layout.
   *
   * @return {Promise} Resolved once the provided layout has been set as the current active layout for the register.
   */
  public setCurrentQuickKeyLayout(quickKeyLayout: {
    id: string
  }): IPromise<unknown> {
    const register = this.getActiveRegister()
    return this.registersResource
      .updateRegister(register, { button_layout_id: quickKeyLayout.id })
      .then(register => registersStore.syncData([register]))
      .catch(error => {
        trackError(
          '[Register Manager] Error setting the current quick key layout',
          { data: { error } }
        )
        return $q.reject(error)
      })
  }

  /**
   * Responsible for enabling or disabling quick keys features for the register.
   *
   * @method enableQuickKeys
   *
   * @param  enable {Boolean}
   *         True if quick keys should be enabled, false otherwise
   *
   * @return {Promise} Resolved once the quick keys has either been enabled or disabled.
   */
  public enableQuickKeys(enable: boolean): IPromise<unknown> {
    const register = this.getActiveRegister()
    const buttonLayoutId = register.button_layout_id
    const data: $TSFixMe = { is_quick_keys_enabled: enable }
    // @todo Fix API. If we don't include the current button layout id for the register (button_layout_id) with the
    // request, the api will set it to null.
    if (buttonLayoutId) {
      data.button_layout_id = buttonLayoutId
    }
    return this.registersResource
      .updateRegister(register, data)
      .then(register => registersStore.syncData([register]))
      .catch(error => {
        trackError(
          '[Register Manager] Error enabling quick keys for register',
          { data: { error } }
        )
        return $q.reject(error)
      })
  }

  /**
   * Get the last used receipt number.
   *
   * @method getLastUsedReceiptNumber
   */
  public getLastUsedReceiptNumber(): number | undefined {
    const registerId = this._activeRegister.id
    const receiptNumbersForRegisters = this.posStateManager.getLastUsedReceiptNumbersForRegisters()

    // Handle transitioning from using the old pos state key (LAST_USED_RECEIPT_NUMBER) to the new
    // (RECEIPT_NUMBERS_FOR_REGISTERS) key. If a receipt number can not be found, fallback to using the old key and
    // update. @todo this can be swept up at a later date (added in v13.0)
    if (
      !receiptNumbersForRegisters ||
      angular.isUndefined(receiptNumbersForRegisters[registerId])
    ) {
      const lastUsedReceiptNumber = this.posStateManager.getLastUsedReceiptNumber()

      // Ensure we use the new RECEIPT_NUMBERS_FOR_REGISTERS key from now on
      this.setLastUsedReceiptNumber(lastUsedReceiptNumber!)
      return lastUsedReceiptNumber
    }
    return receiptNumbersForRegisters[registerId]
  }

  /**
   * Set the last used receipt number.
   *
   * @method setLastUsedReceiptNumber
   *
   * @param lastUsedReceiptNumber {Number}
   *        The last used receipt number
   */
  public setLastUsedReceiptNumber(lastUsedReceiptNumber: number): void {
    const registerId = this._activeRegister.id
    const receiptNumbersForRegisters =
      this.posStateManager.getLastUsedReceiptNumbersForRegisters() || {}
    const lastUsedReceiptNumberForRegister =
      receiptNumbersForRegisters[registerId]
    if (
      angular.isUndefined(lastUsedReceiptNumberForRegister) ||
      lastUsedReceiptNumber > lastUsedReceiptNumberForRegister
    ) {
      this.posStateManager.setLastUsedReceiptNumbersForRegisters({
        ...receiptNumbersForRegisters,
        [registerId]: lastUsedReceiptNumber,
      })
    }
  }

  /**
   * The number of days that the active register has been opened for.
   *
   * @method activeRegisterDaysOpen
   *
   * @return {Number} Days register has been opened for.
   */
  public activeRegisterDaysOpen(): number {
    const activeRegister = this.getActiveRegister()
    const activeOutlet = this.getActiveOutlet()
    const registerOpenTime = activeRegister && activeRegister.register_open_time
    const outletTimeZone = activeOutlet && activeOutlet.time_zone
    if (!registerOpenTime || !outletTimeZone) {
      return 0
    }
    const today = moment().startOf('day').tz(outletTimeZone)
    const lastOpenedDateForRegister = moment(registerOpenTime)
      .startOf('day')
      .tz(outletTimeZone)
    return today.diff(lastOpenedDateForRegister, 'days')
  }

  /**
   * Determine if the open register reminder needs to be shown.
   *
   * @method _isRegisterOpenReminderNeeded
   */
  public isRegisterOpenReminderNeeded(): boolean {
    const activeRegisterNotOpenedToday = this.activeRegisterDaysOpen() > 0
    return Boolean(
      this.isActiveRegisterOpen() &&
        activeRegisterNotOpenedToday &&
        !this.isRegisterOpenReminderSeen()
    )
  }

  /**
   * Establish if the register open reminder has been seen.
   *
   * @method isRegisterOpenReminderSeen
   *
   * @return {Boolean} True if the reminder has been seen, false otherwise.
   */
  public isRegisterOpenReminderSeen(): boolean {
    const activeRegister = this.getActiveRegister()
    const registerId = activeRegister.id
    const registerOpenSequenceId = activeRegister.register_open_sequence_id
    const allRegisterOpenReminders = this.posStateManager.getAllOpenRegisterRemindersSeen()
    const registerOpenReminder = allRegisterOpenReminders[registerId]
    return Boolean(
      registerOpenReminder && registerOpenReminder === registerOpenSequenceId
    )
  }

  /**
   * Set the register open reminder flag for the current register and its opening sequence.
   *
   * @method setOpenRegisterReminderSeen
   *
   * @param {Object} params
   *
   *        @param params.registerId
   *               The register id to whose register open reminder needs to be cleared.
   *
   *        @param params.registerOpenSequenceId
   *               The register open sequence id for the register.
   */
  public setOpenRegisterReminderSeen({
    registerId,
    registerOpenSequenceId,
  }: {
    registerId: string
    registerOpenSequenceId: string
  }): void {
    const allOpenRegisterReminderSeen = this.posStateManager.getAllOpenRegisterRemindersSeen()
    allOpenRegisterReminderSeen[registerId] = registerOpenSequenceId
    this.posStateManager.setAllOpenRegisterRemindersSeen(
      allOpenRegisterReminderSeen
    )
    this.emit('closeRegisterReminderSeen', { registerId })
  }

  /**
   * Clear the register reminder open flag for the current register and its opening sequence.
   *
   * @method clearRegisterOpenReminderSeen
   *
   * @param {Object} params
   *
   *        @param params.registerId
   *               The register id to whose register open reminder needs to be cleared.
   */
  public clearRegisterOpenReminderSeen({
    registerId,
  }: {
    registerId: string
  }): void {
    const allOpenRegisterReminderSeen = this.posStateManager.getAllOpenRegisterRemindersSeen()
    if (allOpenRegisterReminderSeen[registerId]) {
      delete allOpenRegisterReminderSeen[registerId]
      this.posStateManager.setAllOpenRegisterRemindersSeen(
        allOpenRegisterReminderSeen
      )
    }
  }

  // ============================== Private API ============================== //
  /**
   * Get all users from indexDB by active outlet.
   *
   * @method _getAllUsersByActiveOutlet
   *
   * @param {String} params.outletId
   *        The active outlet id
   * @return {Promise} A promise resolved with the sorted users list from current outlet
   */
  private _getAllUsersByActiveOutlet(outletId: string): IPromise<ApiUser[]> {
    return this.usersStore
      .all()
      .then((users: ApiUser[]) => {
        const filteredUsers = users.filter(user => {
          if (
            !user.restricted_outlet_ids ||
            !Array.isArray(user.restricted_outlet_ids)
          ) {
            user.restricted_outlet_ids = []
          }
          return (
            user.restricted_outlet_ids.length === 0 ||
            user.restricted_outlet_ids.includes(outletId)
          )
        })
        return sortBy(filteredUsers, (user: ApiUser) => {
          return user.username.toLowerCase()
        })
      })
      .catch(error => {
        trackError('[Register Manager] Failed to get all users', {
          data: { error, outletId },
        })
        return []
      })
  }

  /**
   * Helper to retrieve a specified outlet attribute.
   *
   * @param {String} requestedAttribute
   *        The name of the attribute to retrieve
   *
   * @returns {Object | undefined} The key, value of the outlet attribute.
   */
  private _getActiveOutletAttribute(requestedAttribute: string) {
    if (!this._activeOutlet || !this._activeOutlet.attributes) {
      return
    }
    const outletAttribute = this._activeOutlet.attributes.find(
      (attribute: $TSFixMe) =>
        attribute.key === requestedAttribute ||
        attribute.name === requestedAttribute
    )
    return outletAttribute && outletAttribute.value
  }

  /**
   * Set the active register to the given register and emit the `activeRegisterChange` event.
   *
   * @private
   * @method _setActiveRegister
   *
   * @param  register {Register}
   *         The register to set as the active register
   */
  private _setActiveRegister(register: ApiRegister) {
    const previousActiveRegisterId =
      this._activeRegister && this._activeRegister.id
    if (register !== this._activeRegister) {
      this._activeRegister = register
      // Only emit the registerIdChange event if the register was previously set
      if (
        previousActiveRegisterId &&
        previousActiveRegisterId !== register.id
      ) {
        this.emit('registerIdChange', {
          register: this._activeRegister,
        })
      }
      this.emit('activeRegisterChange', {
        activeRegister: register,
      })
      // Set context to ensure UUIDs generated by the same register have the same node ID
      uuid.setContext(register.id)
      if (register.is_open || this._emulateRegisterOpen) {
        this._emitActiveRegisterOpen(register)
      }
    }
  }

  /**
   * Set the active outlet to the given outlet and emit the `activeOutletChange` event.
   *
   * @private
   * @method _setActiveOutlet
   *
   * @param  outlet {Outlet}
   *         The outlet to set as the active outlet
   */
  private _setActiveOutlet(outlet: ApiOutlet) {
    if (outlet !== this._activeOutlet) {
      this._activeOutlet = outlet
      this.vdCurrencySettings.update({ symbol: outlet.currency_symbol })
      this.emit('activeOutletChange', {
        activeOutlet: outlet,
      })
    }
  }

  /**
   * Set list of all users of current register/outlet.
   *
   * @private
   * @method _setAllUsers
   *
   * @param  users {ApiUser[]}
   *         The users to set as allUsersList
   */
  private _setAllUsers(users: ApiUser[]) {
    this._allUsersList = users
  }

  /**
   * Handle the `sync` event fired by the outletsStore. Check if the current active outlet was updated and
   * update our reference to it if it was.
   *
   * @private
   * @method _onOutletsSync
   *
   * @param  event {Object}
   *         Details of the event
   */
  private _onOutletsSync(event: any) {
    let currentOutlet
    if (event.updated && event.updated.length) {
      // Look for the current outlet in the collection of updated outlets
      currentOutlet = find(event.updated, {
        id: this.posStateManager.getOutletId(),
      })
      if (currentOutlet) {
        this._setActiveOutlet(currentOutlet)
      }
    }
  }

  /**
   * Handle the `sync` event fired by the registersStore. Check if the current active register was updated and
   * update our reference to it if it was.
   *
   * @private
   * @method _onRegistersSync
   *
   * @param  event {Object}
   *         Details of the event
   */
  private _onRegistersSync(event: any) {
    const activeRegister = this._activeRegister
    let updatedActiveRegister
    if (event.updated && event.updated.length) {
      // Look for the current register in the collection of updated registers
      updatedActiveRegister = find(event.updated, {
        id: this.posStateManager.getRegisterId(),
      })
      if (updatedActiveRegister) {
        this._setActiveRegister(updatedActiveRegister)
        this.setLastUsedReceiptNumber(this._activeRegister.invoice_sequence)
        const activeRegisterOpen = this.isActiveRegisterOpen()
        if (activeRegisterOpen !== activeRegister.is_open) {
          if (activeRegisterOpen) {
            this._emitActiveRegisterOpen(updatedActiveRegister)
          } else {
            this._emitActiveRegisterClose(updatedActiveRegister)
          }
        }
      }
    }
  }

  /**
   * Handle the `sync` event fired by the usersStore. Check if any user was updated and
   * update _allUsersList reference.
   *
   * @private
   * @method _onUsersSync
   *
   * @param  event {Object}
   *         Details of the event
   */
  private _onUsersSync(event: any) {
    const activeOutletId =
      this._activeOutlet?.id || this.posStateManager.getOutletId()
    if (event.updated && event.updated.length && activeOutletId) {
      this._getAllUsersByActiveOutlet(activeOutletId).then(users =>
        this._setAllUsers(users)
      )
    }
  }

  /**
   * Emit the `activeRegisterOpen` event for the given opened register.
   *
   * @private
   * @method _emitActiveRegisterOpen
   *
   * @param  openedRegister {Register}
   *         The register that was opened
   *
   * @return {Register} The register that was opened
   */
  private _emitActiveRegisterOpen(openedRegister: ApiRegister) {
    this.emit('activeRegisterOpen', {
      register: openedRegister,
    })
    return openedRegister
  }

  /**
   * Emit the `activeRegisterClose` event for the given closed register.
   *
   * @private
   * @method _emitActiveRegisterClose
   *
   * @param  closedRegister {Register}
   *         The register that was closed
   *
   * @return {Register} The register that was closed
   */
  private _emitActiveRegisterClose(closedRegister: ApiRegister) {
    this.emit('activeRegisterClose', {
      register: closedRegister,
    })
    return closedRegister
  }

  /**
   * Update the receipt settings each time the active register changes
   *
   * @private
   * @method _onActiveRegisterChange
   */
  private _onActiveRegisterChange() {
    return this.waitForSelectedRegister()
      .then(() => this._waitForReceiptStoresToRefresh)
      .then(() => this.receiptSettingsStore.all())
      .then(receiptSettings => this._updateReceiptSettings(receiptSettings))
  }

  /**
   * Handle training mode being toggled on/off by adding/removing the register open emulation.
   *
   * @private
   * @method _onTrainingModeEnabledChange
   *
   * @param  event {Event}
   *         Change event details
   */
  private _onTrainingModeEnabledChange(event: { newValue: $TSFixMe }) {
    const isActiveRegisterOpenBefore = this.isActiveRegisterOpen()
    this._emulateRegisterOpen = event.newValue
    const isActiveRegisterOpen = this.isActiveRegisterOpen()
    if (isActiveRegisterOpen !== isActiveRegisterOpenBefore) {
      if (isActiveRegisterOpen) {
        this._emitActiveRegisterOpen(this._activeRegister)
      } else {
        this._emitActiveRegisterClose(this._activeRegister)
      }
    }
  }

  /**
   * Handle the `sync` event fired by the receiptSettingsStore. Check if they were updated
   * and update our reference if they were.
   *
   * @private
   * @method _onReceiptSettingsSync
   *
   * @param event  {Event}
   *        Sync event details
   */
  private _onReceiptSettingsSync(event: SyncData<$TSFixMe>) {
    if (this._activeRegister && event.updated && event.updated.length) {
      this._updateReceiptSettings(event.updated)
    }

    if (
      this.vdFeatureManager.get(FEATURE_XMEN2565_RECEIPT_WEB_PRESENCE) &&
      this._activeOutlet &&
      event.updated &&
      event.updated.length
    ) {
      const activeTemplateId = this._activeRegister?.receipt_template_id
      const matchingSettings = event.updated.find(
        (s: any) => s.id === activeTemplateId
      )
      const webPresenceItems: WebPresenceItem[] =
        matchingSettings?.web_presence ?? []
      const enabledItems = webPresenceItems.filter(
        (item: WebPresenceItem) =>
          item.is_enabled !== false && item.show_qr_code
      )
      if (enabledItems.length > 0) {
        preCacheQrForItems(enabledItems, this._activeOutlet.name)
      }
    }
  }

  /**
   * Set the receipt settings for the active register by finding the receipt from the receipt templates store.
   *
   * @private
   * @method _updateReceiptSettings
   *
   * @param {Array} receiptSettingss
   *        The receipt templates from the store
   */
  private _updateReceiptSettings(receiptSettingss: ApiReceiptSettings[] = []) {
    const activeRegisterReceiptTemplateId = this._activeRegister
      .receipt_template_id
    const settings = find(receiptSettingss, {
      id: activeRegisterReceiptTemplateId,
    })
    if (settings && settings.settings) {
      this._activeRegisterReceiptLogo = settings.settings.LogoURL
      cleanExtend(this._activeRegisterReceiptSettings, settings.settings)
    }
  }

  /**
   * Sort a registersByOutlet collection by outlet name. Also ensures each register has a related outlet (its
   * possible to delete an outlet without deleting the registers)
   *
   * @private
   * @method _sortRegistersByOutlet
   *
   * @return {Array} An sorted collection of registers by outlet
   */
  private _sortRegistersByOutlet(registersByOutlet: $TSFixMe) {
    const withRegistersSorted = filter(
      registersByOutlet,
      outletRegisterInfo => {
        const { registers, outlet } = outletRegisterInfo
        if (!outlet) {
          trackError('[Register Manager] Registers missing related outlet', {
            data: { registers },
          })
          return false
        }
        outletRegisterInfo.registers = sortBy(registers, 'name')
        return true
      }
    )
    return sortBy(
      withRegistersSorted,
      outletRegisterInfo => outletRegisterInfo.outlet.name
    )
  }
}

/**
 * Responsible for managing registers.
 *
 * @module registerManager
 */
angular
  .module('webregister.managers')
  .service('registerManager', RegisterManager)
